import { lazy, useEffect } from 'react'
import { ConnectedProps, connect, shallowEqual } from 'react-redux'
import { Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom'
//@ts-ignore
import { Confirmation } from '@valerahealth/ui-core'
import {
  Notifications as AlertNotifications,
  CenteredSpinner,
  LazyComponent,
  TaskSearchGrid,
} from '@valerahealth/ui-components'
import { Permission, checkPermissions } from '@valerahealth/redux-auth'
import { RootState, useReduxSelector } from 'redux/store'
import {
  configApiProgramSettingsSelector,
  mainNavigationLayoutSelector,
  patientNavigationLayoutSelector,
  userAdminSelector,
} from 'redux/selectors'
import { SchedulingSidebar } from '@valerahealth/scheduling'
import { CMPatientSearchGrid } from 'components/PatientSearch'
import ProviderCaseload from '../Caseload'
import TreatmentRoom from '../TreatmentRoom'
import NavHeader from '../NavHeader/NavHeader'
import PageNotFound from '../PageNotFound'
import ViewItemPopup from '../ViewItemPopup'
import UserMessages from '../common/UserMessage/userMessages'
import { receiveNewChannelMessage } from '../../redux/actions/treatmentRoom'
import { receiveNewNotification } from '../../redux/actions/caseloadActions'
import {
  hideConfirmation,
  cancelEditOOO,
} from '../../redux/actions/globalActions'
import ErrorPage from '../ErrorPage'
import EditPatientPopup from '../common/EditPatientPopup'
import UserProfile from '../UserProfile'
import { planType, routesEnum } from '../utilities/enums'
import './style.less'
import EditDatesPopup from '../UserProfile/EditDatesPopup'
import TreatmentTimeline from '../TreatmentTimeline'
import TreatmentBasicInfo from '../TreatmentBasicInfo'
import RestorePatient from '../common/RestorePatient'
import Calendar, { selectCanViewCalendar } from '../Calendar'

const PlanTemplate = lazy(() => import('../PlanTemplate'))
const TreatmentBilling = lazy(() => import('../TreatmentBilling'))
const TreatmentAlerts = lazy(() => import('../TreatmentAlerts'))
const CareTeamNew = lazy(() => import('../CareTeamNew'))
const CareTeam = lazy(() => import('../CareTeam'))
const TreatmentDocuments = lazy(() => import('../TreatmentDocuments'))
const TreatmentData = lazy(() => import('../TreatmentData'))
const TreatmentAppointments = lazy(() => import('../TreatmentAppointments'))
const LegacyTreatmentAppointments = lazy(
  () => import('../LegacyTreatmentAppointments'),
)
const TreatmentNotes = lazy(() => import('../TreatmentNotes'))
const EditNote = lazy(() => import('../TreatmentNotes/EditNote'))
const Supervision = lazy(() => import('../Supervision'))
const StaffCaseload = lazy(() => import('../StaffCaseload'))
const Reports = lazy(() => import('../Reports'))

const PROJECT_DISPLAY_NEW_CARE_TEAM_TAB =
  process.env.PROJECT_DISPLAY_NEW_CARE_TEAM_TAB === 'true'

function mapStateToProps(state: RootState) {
  const canViewCalendar = selectCanViewCalendar(state)

  const [search, tasks, calendar] = checkPermissions(state, [
    Permission.LandingPage_CmPatientsearch,
    Permission.LandingPage_CmTasks,
    Permission.LandingPage_CmCalendar,
  ])

  let landingPage: string = routesEnum.CASELOAD
  switch (true) {
    case calendar && canViewCalendar && process.env.PROJECT_PROVIDER_CALENDAR:
      landingPage = routesEnum.CALENDAR
      break
    case search:
      landingPage = routesEnum.PATIENT
      break
    case tasks:
      landingPage = routesEnum.TASKS
      break
    default:
      break
  }

  return {
    landingPage,
    confirmation: state.global.confirmation,
    displayError: state.global.displayError,
    userMessageArray: !!state.global.userMessageArray?.length,
    toolsTemplates: state.toolsTemplates,
    editedPatient: state.treatmentRoom.editedPatient,
    editedOOO: state.provider.editedOOO,
    restorePopup: state.treatmentRoom.restorePopup,
  }
}

const mapDispatchToProps = {
  receiveNewChannelMessage,
  receiveNewNotification,
  hideConfirmation,
  cancelEditOOO,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type ReduxProps = ConnectedProps<typeof connector>

function AwaitRouteAccess({
  children,
  isLoading,
  hide,
}: {
  children: JSX.Element
  isLoading: boolean
  hide?: boolean
}) {
  if (isLoading) return <CenteredSpinner />
  if (hide) return <PageNotFound />
  return children
}

function DashboardApp({
  landingPage,
  confirmation,
  userMessageArray,
  displayError,
  editedPatient,
  editedOOO,
  restorePopup,
  hideConfirmation,
  cancelEditOOO,
}: ReduxProps) {
  /** QUERIES DISPATCHED FROM MIDDLEWARE ON LOGIN */

  const {
    permissions,
    isLoading: isPermissionsLoading,
  } = useReduxSelector(userAdminSelector)

  const {
    data: configProgramSettingsRes,
    isConfigProgramSettingsLoading,
    mainLayout,
    patientLayout
  } = useReduxSelector((state) => {
    const { data, isLoading, isUninitialized } =
      configApiProgramSettingsSelector(state)

    const mainLayout = mainNavigationLayoutSelector(state)
    const patientLayout = patientNavigationLayoutSelector(state)

    return {
      data: data?.getProgramSettings,
      isConfigProgramSettingsLoading: isLoading || isUninitialized,
      mainLayout,
      patientLayout,
    }
  }, shallowEqual)

  const isWellness = configProgramSettingsRes?.flags?.isWellness

  const displayNote = !patientLayout.noteHidden && permissions?.accessToNotes
  const displayNoteLoading =
    isPermissionsLoading || isConfigProgramSettingsLoading

  /** END QUERIES DISPATCHED FROM MIDDLEWARE ON LOGIN */

  const { pathname } = useLocation()

  const planTemplates = <LazyComponent Component={PlanTemplate} />

  return (
    <div className="dashboard-app">
      {restorePopup?.display && <RestorePatient {...restorePopup.details} />}
      {editedOOO && (
        <EditDatesPopup
          {...editedOOO}
          handleClose={() => {
            cancelEditOOO()
          }}
        />
      )}
      {confirmation.display && (
        <Confirmation
          details={confirmation.details}
          callback={confirmation.callback}
          onClosed={hideConfirmation}
          btns={confirmation.btns}
        />
      )}
      {userMessageArray && <UserMessages />}
      {displayError && <Navigate to="/error" replace />}
      <ViewItemPopup />
      {editedPatient && <EditPatientPopup />}
      {!~pathname.indexOf('/telehealth') && <NavHeader />}
      <div className="app-content">
        <AlertNotifications />
        <Routes>
          <Route
            element={
              <>
                <Outlet />
                <SchedulingSidebar />
              </>
            }
          >
            <Route
              path="templates/"
              element={
                <AwaitRouteAccess
                  isLoading={isConfigProgramSettingsLoading}
                  hide={mainLayout.tracksHidden}
                >
                  {planTemplates}
                </AwaitRouteAccess>
              }
            >
              <Route path=":subMenuTemplate/" element={planTemplates}>
                <Route path=":selectedId" element={planTemplates} />
              </Route>
              <Route
                path="*"
                element={<Navigate to={planType.POPULATION} replace />}
              />
            </Route>
            <Route
              path="caseload"
              element={
                <AwaitRouteAccess
                  isLoading={isConfigProgramSettingsLoading}
                  hide={mainLayout.caseloadHidden}
                >
                  <ProviderCaseload />
                </AwaitRouteAccess>
              }
            />
            <Route
              path="patients/*"
              element={
                <AwaitRouteAccess
                  isLoading={isConfigProgramSettingsLoading}
                  hide={mainLayout.patientsHidden}
                >
                  <CMPatientSearchGrid />
                </AwaitRouteAccess>
              }
            />
            <Route
              path="tasks/*"
              element={
                <AwaitRouteAccess
                  isLoading={isConfigProgramSettingsLoading}
                  hide={mainLayout.tasksHidden}
                >
                  <TaskSearchGrid
                    formatPatientLink={(id) =>
                      `/caseload/treatment/${id}/basic-info`
                    }
                  />
                </AwaitRouteAccess>
              }
            />
            <Route
              path="/caseload/treatment/:treatmentId"
              element={
                <AwaitRouteAccess
                  isLoading={
                    isConfigProgramSettingsLoading || isPermissionsLoading
                  }
                >
                  <TreatmentRoom />
                </AwaitRouteAccess>
              }
            >
              <Route index element={<Navigate to="timeline" />} />
              <Route path="timeline" element={<TreatmentTimeline />} />
              <Route path="basic-info" element={<TreatmentBasicInfo />} />
              <Route
                path="billing"
                element={
                  <AwaitRouteAccess
                    isLoading={isConfigProgramSettingsLoading}
                    hide={!isWellness}
                  >
                    <LazyComponent Component={TreatmentBilling} />
                  </AwaitRouteAccess>
                }
              />
              <Route
                path="alerts"
                element={<LazyComponent Component={TreatmentAlerts} />}
              />
              <Route
                path="careTeamNew"
                element={
                  <AwaitRouteAccess
                    isLoading={false}
                    hide={!PROJECT_DISPLAY_NEW_CARE_TEAM_TAB}
                  >
                    <LazyComponent Component={CareTeamNew} />
                  </AwaitRouteAccess>
                }
              />
              <Route
                path="careteam"
                element={<LazyComponent Component={CareTeam} />}
              />
              <Route
                path="documents"
                element={<LazyComponent Component={TreatmentDocuments} />}
              />
              <Route
                path="data"
                element={<LazyComponent Component={TreatmentData} />}
              />
              <Route
                path="appointments"
                element={
                  <AwaitRouteAccess
                    isLoading={isConfigProgramSettingsLoading}
                    hide={false}
                  >
                    <LazyComponent
                      Component={
                        configProgramSettingsRes?.appointmentSettings?.flags
                          ?.useSchedulingAppt
                          ? TreatmentAppointments
                          : LegacyTreatmentAppointments
                      }
                    />
                  </AwaitRouteAccess>
                }
              />
              <Route path="plan/*" element={planTemplates}>
                <Route path=":selectedId" element={planTemplates} />
              </Route>
              <Route
                path="note"
                element={
                  <AwaitRouteAccess
                    isLoading={displayNoteLoading}
                    hide={!displayNote}
                  >
                    <LazyComponent Component={TreatmentNotes} />
                  </AwaitRouteAccess>
                }
              />
              <Route
                path="note/:noteId/:type?"
                element={
                  <AwaitRouteAccess
                    isLoading={displayNoteLoading}
                    hide={!displayNote}
                  >
                    <LazyComponent Component={EditNote} />
                  </AwaitRouteAccess>
                }
              />
            </Route>
            <Route
              path="supervision/*"
              element={
                <AwaitRouteAccess
                  isLoading={
                    isPermissionsLoading || isConfigProgramSettingsLoading
                  }
                  hide={mainLayout.supervisionHidden || !permissions?.isSupervisor}
                >
                  <LazyComponent Component={Supervision} />
                </AwaitRouteAccess>
              }
            >
              <Route
                path="staff"
                element={<LazyComponent Component={StaffCaseload} />}
              />
              <Route
                path="reports"
                element={<LazyComponent Component={Reports} />}
              />
              <Route path="*" element={<Navigate to="staff" />} />
            </Route>
            <Route path="/profile" element={<UserProfile />} />
            <Route
              path="/calendar"
              element={
                <AwaitRouteAccess
                  isLoading={isConfigProgramSettingsLoading}
                  hide={mainLayout.calendarHidden}
                >
                  <Calendar />
                </AwaitRouteAccess>
              }
            />
            <Route path="/loading" element={<CenteredSpinner />} />
            <Route path="/error" element={<ErrorPage />} />
            <Route path="/404page" element={<PageNotFound />} />
            <Route path="*" element={<Navigate to={landingPage} replace />} />
          </Route>
        </Routes>
      </div>
    </div>
  )
}

export default connector(DashboardApp)
